import Luminary from '../index';
import type { OpaqueVideoData } from '../video-data/types';
import { Config } from '../video-data/config';

export type SlideRange = {
    start: number;
    end: number;
};

type SliceResult = {
    slideRange: SlideRange;
    startTime: number;
    endTime: number;
    videoData: OpaqueVideoData;
};

function isWithinTolerance(value, target, tolerance) {
    return Math.abs(value - target) <= tolerance;
}

/**
 * VideoDataSlicer
 */
export class VideoDataSlice {
    #startTimeMS = 0;
    #endTimeMS = 1;
    #fps: number;
    dataLuminary: Luminary;

    private constructor(
        videoData: OpaqueVideoData,
        startTime: number,
        endTime: number,
        fps: number,
    ) {
        this.#startTimeMS = startTime;
        this.#endTimeMS = endTime;
        this.#fps = fps;
        this.dataLuminary = new Luminary({
            fetchResources: false,
            isCloudBrowser: true,
            disableRenderer: true,
        });
        this.dataLuminary.load(videoData);

        // Our video renderer doesn't support scene groups with no scenes.
        if (
            this.dataLuminary.sceneGroups.some((sceneGroup: any) => sceneGroup.scenes.length === 0)
        ) {
            new Error('Video contains scene groups with no scenes');
        }
    }

    slice(): SlideRange {
        console.log('startTime', this.#startTimeMS);
        console.log('endTime', this.#endTimeMS);
        const numScenes = this.dataLuminary.scenes.length;
        console.log('numScenes', numScenes);
        const duration = this.dataLuminary.duration;

        if (this.#startTimeMS > duration) {
            throw Error(
                `startTimeMS has a value of ${this.#startTimeMS} which is larger than duration of ${duration}. Unable to continue`,
            );
        }

        console.log({ duration });
        if (
            this.#endTimeMS < 0 ||
            this.#endTimeMS > duration ||
            isWithinTolerance(this.#endTimeMS, duration, Config.VIDEO_TIME_PRECISION)
        ) {
            this.#endTimeMS = duration;
        }

        // This is a hack that we probably should fix later.
        // Currently, we have issues both with the captions timing and the TH media video
        // timing when slicing scenes. This is because the duration of the scene group changes
        // as we remove scenes and the caption animations are relative to the sceneGroup,
        // same for the scene group media (voiceover/talking head) that we try to seek.
        const firstScene = this.dataLuminary.getActiveScenesAt(this.#startTimeMS)[0];
        let timeOffsetFromSlicing = firstScene.startTime;
        let firstSceneIndex = firstScene.globalIndex;
        if (firstScene.sceneGroup.mediaAsset || firstScene.sceneGroup.voiceoverAsset) {
            if (firstScene.localIndex > 0) {
                this.dataLuminary.batchRun(() => {
                    const virtualSceneDuration = firstScene.startTimeRelativeToSceneGroup;
                    const indexOfFirstScene = firstScene.localIndex;
                    const firstSceneVideoData = firstScene.prevScene.save();
                    const sceneIdsToRemove = firstScene.sceneGroup.scenes
                        .filter((scene, index) => index < indexOfFirstScene)
                        .map((scene) => scene.id);
                    sceneIdsToRemove.forEach((sceneId) => {
                        firstScene.sceneGroup.removeScene(sceneId);
                    });

                    const virtualScene = firstScene.sceneGroup.addScene({
                        data: firstSceneVideoData,
                    });
                    virtualScene.duration = virtualSceneDuration;

                    firstScene.sceneGroup.moveSceneToIndex(virtualScene.id, 0);
                    firstSceneIndex = virtualScene.globalIndex;
                    timeOffsetFromSlicing = virtualScene.startTime;
                });
            }
            console.log(`first scene is TH, starting from ${firstScene.globalIndex}`);
        }

        // end
        let lastSlideIndex: number;
        if (this.#endTimeMS === duration) {
            lastSlideIndex = this.dataLuminary.scenes.length - 1;
        } else {
            const activeScenes = this.dataLuminary.getActiveScenesAt(this.#endTimeMS);
            lastSlideIndex = activeScenes[activeScenes.length - 1].globalIndex;
        }

        // make sure the last slide index isn't part of a scene group, otherwise it will change the duration
        // which will change the timing of the captions and the TH media
        const lastScene = this.dataLuminary.scenes[lastSlideIndex];
        if (lastScene.sceneGroup.mediaAsset || lastScene.sceneGroup.voiceoverAsset) {
            if (lastScene.localIndex < lastScene.sceneGroup.scenes.length - 1) {
                this.dataLuminary.batchRun(() => {
                    const indexOfLastScene = lastScene.localIndex;
                    const nextSceneVideoData = lastScene.nextScene.save();
                    const sceneIdsToRemove = lastScene.sceneGroup.scenes
                        .filter((scene, index) => index > indexOfLastScene)
                        .map((scene) => scene.id);
                    const durationOfScenesAfterLastScene = lastScene.sceneGroup.scenes
                        .filter((scene, index) => index > indexOfLastScene)
                        .reduce((sum, scene) => sum + scene.duration, 0);
                    sceneIdsToRemove.forEach((sceneId) => {
                        lastScene.sceneGroup.removeScene(sceneId);
                    });

                    const virtualScene = lastScene.sceneGroup.addScene({
                        data: nextSceneVideoData,
                    });
                    virtualScene.duration = durationOfScenesAfterLastScene;
                    lastSlideIndex = virtualScene.globalIndex;
                });
            }

            console.log(`last scene is TH, starting from its last scene ${lastSlideIndex}`);
        }

        // Update start and stop seconds to reflect the global time shift after slicing
        this.#startTimeMS = this.#startTimeMS - timeOffsetFromSlicing;
        this.#endTimeMS = this.#endTimeMS - timeOffsetFromSlicing;

        // Round times up to nearest frame boundary
        const cap = duration - timeOffsetFromSlicing;
        this.#startTimeMS = this._roundUpToFrameBoundary(this.#startTimeMS, cap);
        this.#endTimeMS = this._roundUpToFrameBoundary(this.#endTimeMS, cap);

        return { start: firstSceneIndex, end: lastSlideIndex };
    }

    _roundUpToFrameBoundary(timeMS: number, maxMS: number) {
        const numFrames = Math.ceil((timeMS / 1000) * this.#fps);
        return Math.min((numFrames / this.#fps) * 1000, maxMS);
    }

    get startTime(): number {
        return this.#startTimeMS;
    }

    get endTime(): number {
        return this.#endTimeMS;
    }

    static async slice(
        videoData: OpaqueVideoData,
        startTime: number,
        endTime: number,
        fps: number,
    ): Promise<SliceResult> {
        const slicer = new VideoDataSlice(videoData, startTime, endTime, fps);
        const range = slicer.slice();
        const modifiedVideoData = slicer.dataLuminary.save();

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        await slicer.destroy();

        return {
            slideRange: range,
            startTime: slicer.startTime,
            endTime: slicer.endTime,
            videoData: modifiedVideoData,
        };
    }

    async destroy() {
        await this.dataLuminary.destroy();
    }
}
